import React from 'react';
import { List, Button, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ChatInput from '../Input';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  disconnectSocket,
  initiateSocket,
  sendMessage,
  subscribeToChat,
} from './socket';
import { axios } from '../../../config';
import {
  clearSentMessage,
  conversationReply,
  setSeenMessages,
} from '../actions';
import { Avatar } from '../../index';
import { fixImgPath, mapMessageData } from '../../../utils/helpers';

const { Text } = Typography;

const ChatRoom = ({ chatData }) => {
  const [page, setPage] = React.useState(0);
  const [messages, setMessages] = React.useState([]);
  const [message, setMessage] = React.useState('');
  const [recipient, setRecipient] = React.useState();
  const [recipients, setRecipients] = React.useState([]);
  const [isGroup, setIsGroup] = React.useState(false);
  const [loadingMoreMsg, setLoadingMoreMsg] = React.useState(false);
  const [noMoreMsg, setNoMoreMsg] = React.useState(false);
  const sentMessage = useSelector((s) => s.chat.sentMessage);
  const user = useSelector((s) => s.auth.user.data);
  const mode = useSelector((s) => s.auth.mode);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (chatData?._id) {
      disconnectSocket();
      if (chatData._id) {
        fetchData(chatData._id);
        initiateSocket(chatData._id);
        subscribeToChat((data) => {
          if (data.message) {
            setMessages((oldChats) => [data.message, ...oldChats]);
            dispatchSetSeenMessages([data.message._id]);
          }
        });
      }
    }
  }, [chatData]);

  const fetchData = React.useCallback(
    async (conversationId) => {
      const res = await axios.get('/chat/conversation/' + conversationId);
      setInitialData(res.data);
      setMessages(res.data.messages || []);
      const unseenMessages = res.data.messages
        .filter((m) => m.user !== user._id && !m.seenBy?.includes(user._id))
        .map((m) => m._id);
      dispatchSetSeenMessages(unseenMessages);
    },
    [user._id]
  );

  const dispatchSetSeenMessages = React.useCallback(
    (messageIds) => {
      dispatch(
        setSeenMessages({
          messageIds,
          conversationId: chatData._id,
          userId: user._id,
        })
      );
    },
    [user, chatData]
  );

  const setInitialData = React.useCallback(
    (data) => {
      if (!data.isGroup) {
        const recipientUser = data.users.find((x) => x._id !== user._id);
        setRecipient(recipientUser);
      } else {
        setRecipients(data.users.filter((x) => x._id !== user._id));
        setIsGroup(true);
      }
    },
    [user]
  );

  const appendMessage = React.useCallback(
    (message, cb) => {
      const tempId = Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, '')
        .substr(2, 10);
      setMessages((previousMessages) => [
        {
          _id: tempId,
          ...message,
          createdAt: new Date(),
          user: { _id: user._id, name: user.name, avatar: user.avatar },
        },
        ...previousMessages,
      ]);
      if (cb) cb(tempId);
    },
    [user, recipients, isGroup, recipient]
  );

  const onSend = (message, id) => {
    const dbData = { conversationId: id, messageData: { ...message } };
    dispatch(conversationReply(dbData));
  };

  React.useEffect(() => {
    if (sentMessage) {
      setMessages((messages) =>
        messages.map((msg, i) => (i === 0 ? sentMessage : msg))
      );
      sendMessage({
        ...sentMessage,
        recipientIds: isGroup ? recipients.map((r) => r._id) : [recipient._id],
      });
      dispatch(clearSentMessage());
    }
  }, [sentMessage, isGroup, recipients, recipient]);

  const loadMore = React.useCallback(async () => {
    if (!loadingMoreMsg) {
      const newPage = page + 1;
      setLoadingMoreMsg(true);
      const res = await axios.get(
        `/chat/conversation/${chatData._id}/messages?page=${newPage}`
      );
      setMessages((state) => [...state, ...res.data.messages]);
      setLoadingMoreMsg(false);
      setPage(newPage);
      if (!res.data.messages.length) setNoMoreMsg(true);
    }
  }, [page, chatData, loadingMoreMsg]);

  const renderLoadMoreBtn = React.useMemo(
    () =>
      messages.length > 19 && !noMoreMsg ? (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <Button onClick={loadMore} loading={loadingMoreMsg}>
            Load more
          </Button>
        </div>
      ) : null,
    [messages, loadingMoreMsg, noMoreMsg, page]
  );

  const messagesData = React.useMemo(
    () => mapMessageData(messages),
    [messages]
  );

  const renderMessage = (item) => {
    const isCurrentUser = item.user._id === user._id;
    const messageStyle = {
      display: 'flex',
      justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
      marginBottom: '16px',
      padding: '0 16px',
    };

    const bubbleStyle = {
      maxWidth: '70%',
      padding: '12px 16px',
      borderRadius: '18px',
      backgroundColor: isCurrentUser ? '#1890ff' : '#f0f0f0',
      color: isCurrentUser ? 'white' : 'black',
      position: 'relative',
    };

    const avatarStyle = {
      marginRight: isCurrentUser ? '0' : '8px',
      marginLeft: isCurrentUser ? '8px' : '0',
      order: isCurrentUser ? 2 : 1,
    };

    const contentStyle = {
      order: isCurrentUser ? 1 : 2,
    };

    return (
      <div key={item._id} style={messageStyle}>
        <div style={avatarStyle}>
          <Avatar
            src={item.user.avatar}
            name={item.user.firstName || item.user.name}
            size={32}
          />
        </div>
        <div style={contentStyle}>
          {item.file ? (
            <div style={bubbleStyle}>
              <a
                href={fixImgPath(item.file)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: isCurrentUser ? 'white' : '#1890ff' }}
              >
                {item.fileName}
              </a>
            </div>
          ) : (
            <div style={bubbleStyle}>
              <Text style={{ color: isCurrentUser ? 'white' : 'black' }}>
                {item.text}
              </Text>
            </div>
          )}
          <div
            style={{
              fontSize: '12px',
              color: '#999',
              marginTop: '4px',
              textAlign: isCurrentUser ? 'right' : 'left',
            }}
          >
            {new Date(item.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chatInner" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column-reverse',
          padding: '16px 0',
        }}
      >
        {renderLoadMoreBtn}
        {messagesData.map(renderMessage)}
      </div>
      <ChatInput
        value={message}
        onChange={setMessage}
        onSend={onSend}
        appendMessage={appendMessage}
        chatId={chatData._id}
      />
    </div>
  );
};

export default ChatRoom;